import { Injectable } from '@angular/core';
import { BaseComponent } from '@wms/app/components';
import { BaseVariableService, VARIABLE_CONSTANTS } from '@wms/variables';
import { extractType, isEmptyObject, isString, WS_CONSTANTS } from '@wms/core';
import { includes, without, reject, filter, find, findKey, get, forEach, isArray, isEmpty, every, isNull } from 'lodash-es';
import { ServiceFactoryService, TypeFactoryService } from '@wms/app/services';
import { isObject } from '@wm/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class WmsServiceVariableService extends BaseComponent {
  serviceOperations: any = {};
  sampleResponse: any;
  CONTROLLER_TYPE_QUERY = 'QueryExecution';
  SV_OP_WRAPPER = "_sv_operation_wrapper";
  CONTROLLER_OPERATION_SEPARATOR = "~wm_separator~";
  sampleParamsValues: any;
  bindMapCollection: any;
  HANDLE_SOAP_PARAMS: boolean = false;
  dataTypes: any;
  propertyGroupChangeSubject = new Subject();

  constructor(private baseVariableService: BaseVariableService, private serviceFactory: ServiceFactoryService, private typeFactory: TypeFactoryService) {
    super();
    this.dataTypes = this.typeFactory.getTypes();
  }

  __getVariableDependencies() {
    let variable = this.baseVariableService.selectedVariable.variableObject,
      operationType = variable._operationType,
      serviceType = variable._serviceType,
      rejectionList = [VARIABLE_CONSTANTS.SERVICE_TYPE_WEBSOCKET];

    if (operationType === 'query' || variable.controller === 'QueryExecution') {
      this.baseVariableService.selectedVariable.propertyObj.operation.displayName = this.locale.LABEL_VARIABLE_SELECT_QUERY;
    } else if (operationType === 'procedure' || variable.controller === 'ProcedureExecution') {
      this.baseVariableService.selectedVariable.propertyObj.operation.displayName = this.locale.LABEL_VARIABLE_SELECT_PROCEDURE;
    } else {
      this.baseVariableService.selectedVariable.propertyObj.operation.displayName = 'Method';
    }

    if (serviceType) {
      this.baseVariableService.selectedVariable.propertyObj.service.editonly = false;
      this.baseVariableService.selectedVariable.propertyObj.operation.editonly = false;
      if (includes(VARIABLE_CONSTANTS.REST_SUPPORTED_SERVICES, serviceType)) {
        rejectionList = without(VARIABLE_CONSTANTS.REST_SUPPORTED_SERVICES, serviceType);
        rejectionList.push(VARIABLE_CONSTANTS.SERVICE_TYPE_MODEL);
      }
    } else {
      this.baseVariableService.selectedVariable.propertyObj.service.editonly = true;
    }

    if (operationType === 'databaseapi') {
      this.baseVariableService.selectedVariable.propertyObj.operation.editonly = true;
    } else {
      this.baseVariableService.selectedVariable.propertyObj.operation.editonly = false;
    }

    /*get the list of services available and append the options for service property*/
    (this.serviceFactory.getServicesWithType() as Promise<any>).then((services: any) => {
      services = reject(services, (service) => includes(rejectionList, service.type));
      services.sort((prevItem: any, currentItem: any) => prevItem.name.localeCompare(currentItem.name));

      /*push the services in the options list for the respective property selectbox*/
      this.baseVariableService.selectedVariable.propertyObj.service.options = {};
      services.forEach((serviceObj: any) => {
        this.baseVariableService.selectedVariable.propertyObj.service.options[serviceObj.name] = serviceObj.name;
      });

      /*append the options for operations property based on selected service property*/
      this.__onPropertyChange('service', undefined, true);
    });

    /* apply check for transformationRequired field */
    this.__onPropertyChange('transformationRequired', undefined, true);
  }

  __setServiceOperations(operations: any, selectedVariable: any, initFlag: any, serviceType: any) {
    let operation,
      opController,
      opKey,
      oldOperationObj: any;
    selectedVariable[this.SV_OP_WRAPPER] = undefined;
    oldOperationObj = find(operations, { 'operationId': selectedVariable.variableObject.operationId });
    operations.forEach((operation: any) => {
      //exclude all deprecated operations except current variable's operation
      if (operation.deprecated && operation.operationId !== (oldOperationObj && oldOperationObj.operationId)) {
        return;
      }
      opController = operation.controller ? operation.controller.replace(/Controller$/, '') : '';
      opKey = opController + this.CONTROLLER_OPERATION_SEPARATOR + operation.name;
      selectedVariable.propertyObj.operation.options[opKey] = {
        type: opController,
        name: serviceType.toLowerCase() === 'openapiservice' ? operation.name.replace(selectedVariable.variableObject.service + '_', '').replace(operation.entityName + '_', '') : operation.name
      };
      this.serviceOperations[opKey] = operation;
    });
    /* operation change the modelObj */
    selectedVariable.propertyObj.operation.modelObj = selectedVariable;
    selectedVariable.propertyObj.operation.modelKey = this.SV_OP_WRAPPER;
    if (initFlag && selectedVariable.variableObject['operation'] && selectedVariable.variableObject['operation'] !== '') {
      if (selectedVariable.variableObject.controller) {
        selectedVariable[this.SV_OP_WRAPPER] = selectedVariable.variableObject['controller'] + this.CONTROLLER_OPERATION_SEPARATOR + selectedVariable.variableObject['operation'];
      } else {
        /* backward compatibility, controller not found, find the first one with active variable's operation */
        let indexKey: any = findKey(this.serviceOperations, (op: any) => {
          return op.name === selectedVariable.variableObject['operation'];
        });
        operation = this.serviceOperations[indexKey];
        opController = operation ? operation.controller : '';
        opController = opController ? opController.replace(/Controller$/, '') : '';
        selectedVariable[this.SV_OP_WRAPPER] = opController + this.CONTROLLER_OPERATION_SEPARATOR + selectedVariable.variableObject['operation'];
      }

      // WMS-25864 - selectedVariable & baseVariableService.selectedVariable are differing - cause of which dataTabs are not rendered for service variables.
      if (selectedVariable !== this.baseVariableService.selectedVariable) {
        this.baseVariableService.selectedVariable = selectedVariable;
      }
      this.__onPropertyChange('operation', undefined, true);
    }
  }

  /* function to add a parameter to the selected variable properties & make it visible in the UI */
  __prepareBindingObjectList(param: any, variable: any, oldBindings: any) {
    let paramReference: any,
        paramsCollection = variable.variableObject.dataBinding,
        paramName = param.name || (param.parameterType && param.parameterType.toLowerCase());
    let numericBindings = ['float', 'double'];
    paramName = includes(paramName, '.') ? this.baseVariableService.wrapTargetKey(paramName) : paramName;
    paramsCollection.forEach((paramObject: any) => {
        if (paramObject.target === paramName) {
            paramReference = paramObject;
            if (numericBindings.indexOf(param.format) > -1 && param.parameterType === 'header' && isArray(paramObject.value)) {
                paramReference.value = parseFloat(paramObject.value[0]);
            }
        }
    });
    /* reference not found, add to the fields collection and process internal nodes based on type meta info for param */
    if (!paramReference) {
        paramReference = paramsCollection[paramsCollection.push({
            "target": paramName,
            "type": param.typeRef,
            "isList": param.isList,
            "format": param.format
        }) - 1];
        if (oldBindings[param.name] && !isObject(oldBindings[param.name])) {
            paramReference.value = oldBindings[param.name];
        }

    } else if (paramReference.type !== param.typeRef) {
        /* update the type ref of param in existing reference */
        paramReference.type = param.typeRef;
        paramReference.isList = param.isList;
    }
    paramReference.format = param.format;

    // for params with name not found, add subscript as data type
    if (!param.name) {
        paramReference.subscript = extractType(param.typeRef);
    }
  }

  /**
   * Fetches the rest paramater details
   * @param service
   */
  __assignRestParameterDetails(service: any) {
    this.baseVariableService.disableDialogActions = true;
    this.baseVariableService.getRestServiceDetails(service, (response: any) => {
      if (response && response.httpRequestDetails) {
        forEach(response.httpRequestDetails.queryParams, (value, key) => {
          this.sampleParamsValues[key] = value;
        });
        forEach(response.httpRequestDetails.headers, (value, key) => {
          this.sampleParamsValues[key] = value;
        });
        forEach(response.httpRequestDetails.multiParamInfoList, (param) => {
          this.sampleParamsValues[param.name] = param.testValue;
        });
        if (response.httpRequestDetails.requestBody) {
          this.sampleParamsValues.RequestBody = response.httpRequestDetails.requestBody;
        }
        if (isObject(this.sampleParamsValues)) {
          forEach(this.sampleParamsValues, (value, key) => {
            this.bindMapCollection.push({
              "target": includes(key, '.') ? this.baseVariableService.wrapTargetKey(key) : key,
              "value": value,
              "type": "java.lang.String"
            });
          });
        }
        this.sampleResponse = this.baseVariableService.extractRESTSampleResponseData(response);
        /* setting default sample param values */
        if (this.bindMapCollection && this.bindMapCollection.length) {
          this.baseVariableService.selectedVariable.variableObject.dataBinding = this.bindMapCollection;
        }
      }
      this.baseVariableService.disableDialogActions = false;
    });
  }

  /**
     * Returns true if the binding node target is not a param retrieved from service def, but is a param required for request construction
     * returns true for 'dataBinding'
     * returns true for internal node bindings like body.x, body.x.y, etc
     * returns false for any first level node (without a dot) other than dataBinding
     * @param nodeTarget
     * @returns {boolean|*}
     */
  __isBindingNotFromParam(nodeTarget: any) {
    return nodeTarget === 'dataBinding' || includes(nodeTarget, '.');
  }

  /*function to handle property change for a variable*/
  __onPropertyChange(propertyName: any, value?: any, initFlag?: any) {
    let selectedService,
      selectedOperation: any = '',
      selectedOperationObj,
      serviceType: any,
      selectedVariable = this.baseVariableService.selectedVariable,
      hideDataServiceOptions = true,
      hideTransformationOptions = false,
      returnType,
      isList,
      transformationRequired,
      columnField,
      processColumns,
      targetOperationType,
      isServiceWithOneOperation;

    if (!initFlag) {
      this.baseVariableService.markUpdated();
    }

    selectedService = selectedVariable.variableObject['service'];
    /* check for service type */
    serviceType = (this.serviceFactory.getServiceObjectByName(selectedService) as any).type;


    /*If 'service' property changed, change the operation property as well*/
    if (propertyName === 'service') {
      if (!initFlag) {
        selectedVariable.propertyObj.operation.modelObj[selectedVariable.propertyObj.operation.modelKey] = '';
      }
      selectedVariable.propertyObj.operation.options = [];
      selectedService = selectedVariable.variableObject['service'];

      /* if service not selected for the variable */
      if (!selectedService) {
        this.baseVariableService.emptyDataBindingObject(selectedVariable);
        return;
      }

      /* append the service type to the variable */
      selectedVariable.variableObject['serviceType'] = serviceType || "";

      /* decide on displaying data-transformation related properties */
      hideTransformationOptions = serviceType !== VARIABLE_CONSTANTS.SERVICE_TYPE_REST;
      selectedVariable.propertyObj['transformationRequired'].hide = hideTransformationOptions;
      selectedVariable.variableObject['transformationRequired'] = hideTransformationOptions ? false : selectedVariable.variableObject['transformationRequired'];
      this.__onPropertyChange('transformationRequired', undefined, true);

      /*get the list of operations for the selected service*/
      this.baseVariableService.variableDialogMsg = {
        'type': 'loading',
        'caption': this.locale.MESSAGE_SERVICE_INFO_LOADING
      };
      (this.serviceFactory.getServiceOperations(selectedService) as any).then((operations: any) => {
        this.serviceOperations = {};
        this.baseVariableService.variableDialogMsg = null;
        selectedVariable.propertyObj.operation.options = {};
        targetOperationType = selectedVariable.variableObject._operationType;
        if (targetOperationType) {
          let targetController = targetOperationType === 'query' ? 'QueryExecution' : (targetOperationType === 'procedure' ? 'ProcedureExecution' : '');
          const tmpOperations = filter(operations, { controller: targetController });
          if (tmpOperations.length || targetController) {
            operations = tmpOperations;
          }
        }
        operations.sort((prevItem: any, currentItem: any) => prevItem.name.localeCompare(currentItem.name));
        isServiceWithOneOperation = includes(VARIABLE_CONSTANTS.WEB_SERVICE_VARIABLE_WITH_NO_OPERATION, serviceType);
        selectedVariable.propertyObj.operation.hide = isServiceWithOneOperation;
        if (operations.length === 1 && !initFlag) {
          selectedVariable.variableObject.operation = operations[0].name;
          selectedVariable.variableObject.controller = operations[0].controller;
          initFlag = true;
        }
        this.__setServiceOperations(operations, selectedVariable, initFlag, serviceType);
        if (initFlag === true) {
          this.baseVariableService.sourceExists();
        }
      }).catch(() => {
        this.baseVariableService.sourceExists();
        this.baseVariableService.variableDialogMsg = null;
      });
    }

    /*If the 'operation' property changed, change the data properties*/
    if (propertyName === 'operation' || propertyName === this.SV_OP_WRAPPER) {
      this.baseVariableService.isFileUploadTypeVar(selectedVariable);
      selectedService = selectedVariable.variableObject['service'];
      selectedOperation = selectedVariable[this.SV_OP_WRAPPER];
      selectedOperationObj = get(this.serviceOperations, selectedOperation) || {};

      // For security service variable and database variable of type query api, if the selectedOperation doesn't have the controller and operationId (ex: Security~wm_separator~getAccessToken) in the model but just the operationId (ex: getAccessToken)
      // For Imported API, for other endpoints
      // Fetch the operation object by checking if the key has operationId in it if selectedOperationObj is empty
      if (selectedOperation && !Object.keys(selectedOperationObj).length) {
        let selectedOperationParams = selectedOperation.split('~');
        selectedOperationObj = find(this.serviceOperations, (val, key) => {
          if ( includes(key, selectedOperation) || includes(key, selectedOperationParams[selectedOperationParams.length - 1])) {
            let convertedKey:any = key && key.split(this.CONTROLLER_OPERATION_SEPARATOR).pop();
            let length: number = convertedKey.split('_').length;
            if(selectedOperation === convertedKey.split('_')[length-1] || selectedOperation ===  convertedKey.split(selectedVariable.variableObject['service'] + '_')[1]) {
              selectedOperation = key;
              return true
            }
          }
          return false;
        }) || {};
      }
      this.sampleResponse = undefined;

      /* re-format operation and persist it */
      selectedOperation = selectedOperation && selectedOperation.split(this.CONTROLLER_OPERATION_SEPARATOR).pop();
      selectedVariable.variableObject.operationType = get(selectedOperationObj, 'operationType');
      selectedVariable.variableObject.operation = selectedOperation;
      selectedVariable.variableObject.controller = get(selectedOperationObj, 'controller') ? selectedOperationObj.controller.replace(/Controller$/, '') : '';

      /* decide on displaying query related properties */
      hideDataServiceOptions = selectedVariable.variableObject.controller !== this.CONTROLLER_TYPE_QUERY;
      selectedVariable.propertyObj['maxResults'].hide = hideDataServiceOptions;
      selectedVariable.propertyObj['orderBy'].hide = hideDataServiceOptions;
      //set deprecated for variables which had deprecated operation
      selectedVariable.variableObject._deprecated = selectedOperationObj.deprecated;
      /*check value sanity*/
      if (!selectedService || !selectedOperation || selectedOperation === '') {
        this.baseVariableService.emptyDataBindingObject(selectedVariable);
        return;
      }

      /*get the list of operations for the selected service*/
      this.baseVariableService.variableDialogMsg = {
        'type': 'loading',
        'caption': this.locale.MESSAGE_SERVICE_INFO_LOADING
      };
      this.serviceFactory.getServiceOperationParams(selectedService, selectedOperationObj.operationId).then((operations: any) => {
        this.baseVariableService.variableDialogMsg = null;
        let dataSetSubGroup: any,
          modelNodesArray,
          errorMsg,
          modelNodesObj: any = {},
          oldBindingMap: any = {};
        if (!initFlag) {
          /*initialize the data binding object for the variable*/
          selectedVariable.variableObject.dataBinding = [];
        } else if (isObject(selectedVariable.variableObject.dataBinding) && !Array.isArray(selectedVariable.variableObject.dataBinding)) {
          /*
           * In old implementation, dataBinding is an object map like:
           * dataBinding: {"field1": "value1", "field2": "value2}
           * If such object is found, extract it and persist for migration
           */
          oldBindingMap = selectedVariable.variableObject.dataBinding;
          selectedVariable.variableObject.dataBinding = [];
        }

        this.baseVariableService.emptyDataBindingObject(selectedVariable);

        /* find the dataSet subgroup out of properties */
        forEach(this.baseVariableService.propertyGroups, (parentGroup: any) => {
          forEach(parentGroup.subGroups, (subGroup: any) => {
            if (subGroup.name === "inputfields") {
              dataSetSubGroup = subGroup;
            }
          });
        });

        /* remove previous properties from the dataSet subgroup */
        dataSetSubGroup.properties = [];

        if (serviceType === VARIABLE_CONSTANTS.SERVICE_TYPE_REST && selectedVariable.isNew) {
          this.sampleParamsValues = {};
          this.bindMapCollection = [];
          this.__assignRestParameterDetails(selectedVariable.variableObject.service);
        }


        /*special case for params of a SOAP service*/
        if (this.HANDLE_SOAP_PARAMS) {
          /* loop over each operation parameter */
          forEach(operations.parameter, (parameter: any) => {
            if (parameter.typeRef && this.dataTypes[parameter.typeRef] && this.dataTypes[parameter.typeRef].fields && !isEmptyObject(this.dataTypes[parameter.typeRef].fields)) {
              /* if parameter object has fields inside, push them*/
              Object.keys(this.dataTypes[parameter.typeRef].fields).forEach((subParamName: any) => {
                let subParam: any = this.dataTypes[parameter.typeRef].fields[subParamName];
                this.baseVariableService.addParamToProperties({ name: subParamName, typeRef: subParam.type, isList: subParam.isList }, selectedVariable, dataSetSubGroup, this.dataTypes, parameter.name || (parameter.parameterType && parameter.parameterType.toLowerCase()));
              });
            } else {
              /* else, simply push the parameter */
              if (parameter.parameterType !== 'OUT') {
                this.baseVariableService.addParamToProperties(parameter, selectedVariable, dataSetSubGroup, this.dataTypes);
              }
            }
          });
        } else {
          /* case for service other than SOAP */
          forEach(operations.parameter, (parameter: any) => {
            if (parameter.parameterType !== 'OUT') {
              this.__prepareBindingObjectList(parameter, selectedVariable, oldBindingMap);
            }
          });
        }

        if (selectedVariable.variableObject._deprecated) {
          forEach(selectedVariable.variableObject.dataBinding, (binding) => {
            binding.invalid = !find(operations.parameter, { "name": binding.target }) || undefined;
          });
          errorMsg = isEmpty(operations.parameter) ? this.locale.MESSAGE_DEPRECATED_OPERATION : this.locale.MESSAGE_INVALID_PARAMETER;
          // TO DO
          // errorMsg = errorMsg + '<a target="_blank" style=\"cursor:pointer\" href="' + EXTERNAL_LINK_CONSTANTS.DATABASE_SERVICES + '">More info</a>';
          // $scope.toggleMessage(true, errorMsg, "warn");
        } else {
          // Do not hide a loading message for an async request.
          // if (!$scope.disableDialogActions) { -- TO DO
          //   $scope.toggleMessage(false);
          // }
          /* remove stale bindings */
          selectedVariable.variableObject.dataBinding = reject(selectedVariable.variableObject.dataBinding, (binding) => {
            return (!this.__isBindingNotFromParam(binding.target) && find(operations.parameter, { "name": binding.target }) === undefined);
          });

          // Update data types for inner referenced nodes.
          if (!(includes(WS_CONSTANTS.NON_BODY_HTTP_METHODS, operations.operationType))) {
            selectedVariable.variableObject.dataBinding.forEach((binding: any) => {
              if (!binding.type) {
                binding.type = this.typeFactory.getChildNodeType(operations.parameter[0].typeRef, binding.target.split('.').splice(1).join('.'))
              }
            });
          }
        }

        /* add prepared bindings to the property object for display */
        this.baseVariableService.addBindingsToProperties();

        /* set the type of the variable to the return type of the operation */
        operations.return = operations.return || {};
        returnType = operations.return.typeRef;
        isList = operations.isList || false;
        selectedVariable.variableObject.type = returnType;
        selectedVariable.variableObject.isList = isList;

        /* get the data model based on the return type of service-operation */
        modelNodesArray = this.typeFactory.getFieldsForType(selectedVariable.variableObject.type, { 'filter': 'all' }).sort();

        /* default none value */
        modelNodesObj['--none--'] = "--none--";
        forEach(modelNodesArray, (model) => {
          modelNodesObj[model] = model;
        });

        selectedVariable.propertyObj.columnField.options = modelNodesObj;
        selectedVariable.propertyObj.dataField.options = modelNodesObj;

        if (VARIABLE_CONSTANTS.REST_SUPPORTED_SERVICES.indexOf(serviceType) !== -1) {
          //Marking variable dirty if operationId has got updated
          if (selectedVariable.variableObject.operationId !== operations.operationId) {
            selectedVariable.variableObject.operationId = operations.operationId;
            this.baseVariableService.markUpdated();
          }
        }
      }).catch(() => {
        this.baseVariableService.sourceExists();
        this.baseVariableService.variableDialogMsg = null;
      });
    }

    if (propertyName === 'transformationRequired') {
      transformationRequired = selectedVariable.variableObject['transformationRequired'];
      selectedVariable.propertyObj.columnField.hide = !transformationRequired;
      selectedVariable.propertyObj.dataField.hide = !transformationRequired;

      if (!initFlag && !this.sampleResponse) {
        this.baseVariableService.getRestServiceDetails(selectedVariable.variableObject.service, (response: any) => {
          this.sampleResponse = this.baseVariableService.extractRESTSampleResponseData(response);
        });
      }
    }

    if (propertyName === 'columnField') {
      columnField = selectedVariable.variableObject.columnField;
      selectedVariable.variableObject['transformationColumns'] = [];

      /* if column field invalid, remove transformationColumns field from variable */
      if (!columnField || columnField === '--none--') {
        delete selectedVariable.variableObject['transformationColumns'];
        return;
      }

      processColumns = (columns: any) => {
        /* sanity check */
        if (!Array.isArray(columns) || !isString(columns[0])) {
          return;
        }
        columns = columns.map(function (e) {
          return isString(e) && e.replace(/[^a-zA-Z0-9_]/g, '_');
        });
        selectedVariable.variableObject['transformationColumns'] = columns;
      };

      if (isObject(this.sampleResponse)) {
        processColumns(get(this.sampleResponse, columnField));
      } else {
        // TO DO
        // $scope.showToaster({ type: 'error', title: 'Response Body Error', description: 'This option is applicable only if the target service returns JSON or XML type response body' });
        return;
      }
    }

    /*executing the event change method to create callback variables.*/
    if (VARIABLE_CONSTANTS.EVENTS.indexOf(propertyName) > -1) {
      this.baseVariableService.onEventChange(propertyName, value);
    }

    const isEmptyVal = every(value, v => v === '' || isNull(v))

    if (propertyName === 'spinnerContext' && isEmptyVal) {
      return;
    }
    this.propertyGroupChangeSubject.next(true);
  };
}
